/**
 * Simplified lavendla-tabs.ts
 *
 * Assumptions (enforced server-side):
 *  - Markup structure:
 *      .lavendla-tabs
 *        .lavendla-tabs__nav (optional if only one tab)
 *          a.lavendla-tabs__link (role=tab)
 *        .lavendla-tabs__holder
 *          .lavendla-tabs__panes
 *            .lavendla-tabs__pane (role=tabpanel, aria-hidden)
 *  - Maximum 2 tabs (but logic works for any n >= 1)
 *  - IDs are deterministic from PHP (tab/pane counts match)
 *
 * Responsibilities:
 *  - Apply/maintain ARIA attributes & keyboard navigation
 *  - Toggle active classes and aria-selected / aria-hidden
 *  - No dynamic height calculation (CSS stacking handles layout)
 *
 * Keyboard:
 *  - ArrowLeft / ArrowRight cycle
 *  - Home / End jump
 *  - Enter / Space activate if not already
 *
 * Public API: window.LavTabs.initAll()
 */

const TAB_CONTAINER = '.lavendla-tabs';
const LINK_SELECTOR = '.lavendla-tabs__link';
const PANE_SELECTOR = '.lavendla-tabs__pane';

document.addEventListener('DOMContentLoaded', () => {
  initAll();
});

/**
 * Initialize every tabs container (idempotent).
 */
export function initAll(): void {
  document.querySelectorAll<HTMLElement>(TAB_CONTAINER).forEach(initContainer);
}

function initContainer(container: HTMLElement): void {
  if (container.dataset.tabsReady === '1') return;

  // Legacy services block compatibility:
  // Older markup used .lavendla-tabs__content (no .lavendla-tabs__panes wrapper).
  // If we detect that situation, promote the content element to the panes wrapper
  // so stacking + opacity logic applies without altering PHP template.
  if (!container.querySelector('.lavendla-tabs__panes')) {
    const legacyContent = container.querySelector<HTMLElement>(
      '.lavendla-tabs__content'
    );
    if (
      legacyContent &&
      !legacyContent.classList.contains('lavendla-tabs__panes')
    ) {
      legacyContent.classList.add('lavendla-tabs__panes');
    }
  }

  const links = container.querySelectorAll<HTMLAnchorElement>(LINK_SELECTOR);
  const panes = container.querySelectorAll<HTMLElement>(PANE_SELECTOR);

  // Nothing to do if mismatch or none
  if (!links.length || links.length !== panes.length) {
    container.dataset.tabsReady = '1';
    return;
  }

  // Single pane: just ensure attributes
  if (links.length === 1) {
    primeSingle(links[0], panes[0]);
    container.dataset.tabsReady = '1';
    return;
  }

  primeAria(links, panes);
  wireInteractions(container, links, panes);
  activate(container, links, panes, links[0]); // default first active

  container.dataset.tabsReady = '1';
}

/**
 * Prime ARIA & roles for multi-tab scenario.
 */
function primeAria(
  links: NodeListOf<HTMLAnchorElement>,
  panes: NodeListOf<HTMLElement>
): void {
  const nav = findNav(links);
  if (nav && !nav.getAttribute('role')) nav.setAttribute('role', 'tablist');

  links.forEach((a, i) => {
    a.setAttribute('role', 'tab');
    a.setAttribute('aria-selected', 'false');
    a.tabIndex = -1;

    const pane = panes[i];
    pane.setAttribute('role', 'tabpanel');
    // Ensure ID relations (if server missed them)
    if (!pane.id) pane.id = `lav-tab-pane-${i}`;
    if (!a.id) a.id = `lav-tab-link-${i}`;
    a.setAttribute('aria-controls', pane.id);
    pane.setAttribute('aria-labelledby', a.id);
    pane.setAttribute('aria-hidden', 'true');
  });
}

/**
 * Prime for single pane (no navigation).
 */
function primeSingle(link: HTMLAnchorElement, pane: HTMLElement): void {
  link.setAttribute('role', 'tab');
  link.setAttribute('aria-selected', 'true');
  link.tabIndex = 0;
  pane.setAttribute('role', 'tabpanel');
  if (!pane.id) pane.id = 'lav-tab-pane-0';
  if (!link.id) link.id = 'lav-tab-link-0';
  link.setAttribute('href', `#${pane.id}`);
  link.setAttribute('aria-controls', pane.id);
  pane.setAttribute('aria-labelledby', link.id);
  pane.setAttribute('aria-hidden', 'false');
  pane.classList.add('lavendla-tabs__pane--active');
  link.classList.add('lavendla-tabs__link--active');
}

/**
 * Attach interaction handlers.
 */
function wireInteractions(
  container: HTMLElement,
  links: NodeListOf<HTMLAnchorElement>,
  panes: NodeListOf<HTMLElement>
): void {
  links.forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      activate(container, links, panes, link);
    });

    link.addEventListener('keydown', (e) => {
      const list = Array.from(links);
      const idx = list.indexOf(link);
      if (idx < 0) return;

      let target: number | null = null;
      switch (e.key) {
        case 'ArrowRight':
          target = (idx + 1) % list.length;
          break;
        case 'ArrowLeft':
          target = (idx - 1 + list.length) % list.length;
          break;
        case 'Home':
          target = 0;
          break;
        case 'End':
          target = list.length - 1;
          break;
        case 'Enter':
        case ' ':
        case 'Spacebar':
          activate(container, links, panes, link);
          return;
        default:
          return;
      }

      if (target !== null) {
        e.preventDefault();
        list[target].focus();
        activate(container, links, panes, list[target]);
      }
    });
  });
}

/**
 * Activate one tab.
 */
function activate(
  container: HTMLElement,
  links: NodeListOf<HTMLAnchorElement>,
  panes: NodeListOf<HTMLElement>,
  activeLink: HTMLAnchorElement
): void {
  links.forEach((l, i) => {
    const pane = panes[i];
    const isActive = l === activeLink;
    l.classList.toggle('lavendla-tabs__link--active', isActive);
    l.setAttribute('aria-selected', isActive ? 'true' : 'false');
    l.tabIndex = isActive ? 0 : -1;

    if (!pane.id) pane.id = `lav-tab-pane-${i}`;
    if (!l.id) l.id = `lav-tab-link-${i}`;
    l.setAttribute('href', `#${pane.id}`);
    l.setAttribute('aria-controls', pane.id);
    pane.setAttribute('aria-labelledby', l.id);

    pane.classList.toggle('lavendla-tabs__pane--active', isActive);
    pane.setAttribute('aria-hidden', isActive ? 'false' : 'true');
  });
}

/**
 * Try to find a navigation wrapper from any link.
 */
function findNav(links: NodeListOf<HTMLAnchorElement>): HTMLElement | null {
  if (!links.length) return null;
  let parent: HTMLElement | null = links[0].parentElement;
  while (parent && !parent.classList.contains('lavendla-tabs__nav')) {
    parent = parent.parentElement;
  }
  return parent;
}

// Global (optional) re-init
declare global {
  interface Window {
    LavTabs?: { initAll: () => void };
  }
}

if (!window.LavTabs) {
  window.LavTabs = { initAll };
}

export default { initAll };
